import UserProfile from "./UserProfile";
import {getManager} from "../Manager";
import {IAnyObject, IStringObject} from "../../common/SimpleInterface";
import {isNativeObject, isNumeric, isString} from "../../common/SiteHelper";
import {dispatchOnUserLogInOut} from "../../event/DispatchEvent";
import ApiRequestVO from "../api/ApiRequestVO";
import {getModelFactory} from "../../model-factory/ModelFactory";

export default class UserManager
{
    loggedInApi: boolean = false;
    loggedInLocal: boolean = false;
    profile:UserProfile = new UserProfile();
    loginToken:string = "";
    unregisteredOrganisationAccess:IStringObject = {};
    unreadMessageCount:number = 0;
    organisationID:number = 0;
    didShowRegisterOrganisationPopup:boolean = false;

    constructor()
    {
        let loginToken = localStorage.getItem('login_token');
        this.loginToken = loginToken ? loginToken : "";
        this.determineUnregisteredOrganisationAccess();
    }

    doesRequireLogin():boolean
    {
        return !!this.loginToken && !this.loggedInApi;
    }

    loggedIn():boolean
    {
        return this.loggedInApi || this.loggedInLocal;
    }

    async attemptLogin()
    {
        if(!this.doesRequireLogin()){
            return null;
        }
        const responseVO = await getManager().api.fetchData("/account/profile", "GET", null, true);
        if(responseVO.apiError){
            if(responseVO.invalidCredentials){
                this.logOut();
            } else if(responseVO.hasNoInternetConnection){
                const cachedUserApplied = this.applyCachedUser();
                if(!cachedUserApplied){
                    this.logOut();
                }
            } else {
                return responseVO.apiError.message ? responseVO.apiError.message : "Unknown Error [4]";
            }
        } else {
            let error:string|null = getManager().user.login(responseVO.data);
            if(error){
                return error;
            }
        }
        return null;
    }

    login(user?:any, localUserOnly:boolean = false, token?:any, preventDispatch:boolean = false): string|null
    {
        const wasLoggedIn = this.loggedIn();
        let error:string|null = this.setUser(user, localUserOnly);
        if(token && !error){
            error = this.setLoginToken(token);
        }
        if(error){
            getManager().user.logOut();
            return error;
        }

        if(!localUserOnly){
            getManager().storage.clear("data_model").then(_ => {});
        }

        if(!preventDispatch){
            this.handleUserLogInOut();
        }
        return null;
    }

    logOut(): void
    {
        this.loggedInLocal = false;
        this.loggedInApi = false;
        this.profile = new UserProfile();
        this.loginToken = "";
        this.unreadMessageCount = 0;
        localStorage.removeItem('login_token');
        this.removeCachedSession();
        getManager().navigation.haveInitialData = false;

        this.handleUserLogInOut();
    }

    setUser(user?:any, localUserOnly:boolean = false): string|null
    {
        let userID:number = 0;
        let email:string = "";
        let firstName:string = "";
        let lastName:string = "";
        let unreadMessageCount:number = 0;
        let organisationID:number = 0;

        if(isNativeObject(user)){
            userID = isNumeric(user.id) ? parseInt(user.id) : 0;
            email = isString(user.email) ? user.email : "";
            firstName = isString(user.first_name) ? user.first_name : "";
            lastName = isString(user.last_name) ? user.last_name : "";
            unreadMessageCount = !localUserOnly ? (isNumeric(user.unread_message_count) ? parseInt(user.unread_message_count) : 0) : 0;
            organisationID = isNumeric(user.organisation_id) ? parseInt(user.organisation_id) : 0;
        }

        if(!userID || !email || !(firstName || lastName)){
            return "Invalid User";
        }

        this.loggedInLocal = true;
        this.loggedInApi = !localUserOnly;
        this.profile = new UserProfile();
        this.profile.userID = userID;
        this.profile.email = email;
        this.profile.firstName = firstName;
        this.profile.lastName = lastName;
        this.profile.apiUser = user;
        this.unreadMessageCount = unreadMessageCount;
        this.organisationID = organisationID;
        if(!localUserOnly){
            localStorage.setItem('logged_in_user', JSON.stringify(this.profile.apiUser));
        }
        return null;
    }

    setLoginToken(token?:any): string|null
    {
        if(!isString(token)){
            return "Invalid Token";
        }
        this.loginToken = token;
        localStorage.setItem('login_token', token);
        return null;
    }

    applyCachedUser(): boolean
    {
        const localUserJSON = localStorage.getItem('logged_in_user');
        let didApply = false;
        if(localUserJSON){
            const localUser = JSON.parse(localUserJSON);
            if(localUser){
                const errorMessage = this.login(localUser, true);
                didApply = !errorMessage;
            }
            if(!didApply){
                this.removeCachedSession();
            }

        }
        return didApply
    }

    removeCachedSession(): void
    {
        localStorage.removeItem('logged_in_user');
        getManager().storage.clear("data_model").then(_ => {});
    }

    addUnregisteredOrganisationAccess(organisationID:number, access:string): void
    {
        this.unregisteredOrganisationAccess[organisationID] = access;
        this.saveUnregisteredOrganisationAccess()
    }

    removeAllUnregisteredOrganisationAccess(): void
    {
        for(let organisationID in this.unregisteredOrganisationAccess){
            delete this.unregisteredOrganisationAccess[organisationID];
        }
        this.saveUnregisteredOrganisationAccess()
    }

    removeUnregisteredOrganisationAccess(organisationID:number): void
    {
        delete this.unregisteredOrganisationAccess[organisationID];
        this.saveUnregisteredOrganisationAccess()
    }

    saveUnregisteredOrganisationAccess(): void
    {
        if(Object.keys(this.unregisteredOrganisationAccess).length){
            localStorage.setItem('unregistered_organisation_access', JSON.stringify(this.unregisteredOrganisationAccess));
        } else {
            localStorage.removeItem('unregistered_organisation_access');
        }
    }

    determineUnregisteredOrganisationAccess(): void
    {
        const jsonString = localStorage.getItem('unregistered_organisation_access');
        const object:IAnyObject|null = jsonString ? JSON.parse(jsonString) : null;
        this.unregisteredOrganisationAccess = {};
        if(object && isNativeObject(object)){
            this.unregisteredOrganisationAccess = object;
        }
    }

    addUnregisteredOrganisationAccessToRequest(requestVO:ApiRequestVO, organisationIDs:number|number[]|boolean, isPostHttpMethodAvailable:boolean = false):void
    {
        if(this.loggedIn() || !organisationIDs){
            return;
        }
        if(organisationIDs === true){
            organisationIDs = [];
            for(let key in this.unregisteredOrganisationAccess)
            {
                const organisationID = parseInt(key);
                if(organisationID)
                {
                    organisationIDs.push(organisationID);
                }
            }
        }
        if(!Array.isArray(organisationIDs)){
            organisationIDs = [organisationIDs];
        }
        let organisationAccessTokens:IStringObject = {};
        for(let key in this.unregisteredOrganisationAccess)
        {
            const organisationID = parseInt(key);
            if(organisationIDs.includes(organisationID)){
                organisationAccessTokens[organisationID.toString()] = this.unregisteredOrganisationAccess[key];
            }
        }
        if(!Object.keys(organisationAccessTokens).length)
        {
            return;
        }
        if(Object.keys(organisationAccessTokens).length > 1 && isPostHttpMethodAvailable){
            requestVO.httpMethod = "POST";
        }

        if(requestVO.httpMethod === "POST"){
            requestVO.params.access_tokens = organisationAccessTokens;
        } else {
            requestVO.includeAuthorization = "Organisation " + encodeURIComponent(JSON.stringify(organisationAccessTokens));
        }

    }

    handleUserLogInOut() {
        if(!(this.loggedInLocal && !this.loggedInApi)){
            getManager().storageData.clearStorage();
        }
        getModelFactory().resetFactories();
        dispatchOnUserLogInOut();
    }
}